import { createReducer, on } from '@ngrx/store';

import { SurveyCreationActions } from '../audience/survey-creation-audience.actions';
import {
  DEFAULT_CHILD_INSIGHTS_STATE,
  INITIAL_SURVEY_CREATION_INSIGHTS_STATE,
} from './survey-creation-insights-state.constants';
import { InsightsState } from './survey-creation-insights-state.model';
import {
  AdvancedFilterActions,
  AdvancedFilterApiActions,
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
  SurveyCreationInsightsPreDraftApiActions,
} from './survey-creation-insights.actions';
import {
  patchActiveInsightsChild,
  patchPreDraftInsightsChild,
} from './utils/insights-children.helper';

export const insightsReducer = createReducer(
  INITIAL_SURVEY_CREATION_INSIGHTS_STATE,

  on(
    SurveyCreationInsightsActions.syncActiveChild,
    (state, { activeChildId }): InsightsState => ({
      ...state,
      activeChildId,
    }),
  ),

  on(
    SurveyCreationInsightsApiActions.computeScorePending,
    (state): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        score: { ...child.score, loading: true, error: null },
      })),
  ),

  on(
    SurveyCreationInsightsApiActions.computeScoreSuccess,
    (state, { scorePercentage }): InsightsState =>
      patchActiveInsightsChild(state, {
        score: { scorePercentage, loading: false, error: null },
      }),
  ),

  on(
    SurveyCreationInsightsApiActions.computeScoreFailure,
    (state, { error }): InsightsState =>
      patchActiveInsightsChild(state, {
        score: { scorePercentage: null, loading: false, error },
      }),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilityPending,
    (state): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: { ...child.feasibility, loading: true, error: null },
      })),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilitySuccess,
    (state, { total, quotaGroups, audience, cpi, suppliers }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          total,
          quotaGroups,
          audience,
          cpi,
          suppliers,
          loading: false,
          error: null,
        },
      })),
  ),

  on(
    SurveyCreationInsightsApiActions.recalculateFeasibilityFailure,
    (state, { error }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: { ...child.feasibility, loading: false, error },
      })),
  ),

  on(
    SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftPending,
    (state, { key }): InsightsState =>
      patchPreDraftInsightsChild(state, key, (child) => ({
        feasibility: { ...child.feasibility, loading: true, error: null },
      })),
  ),

  on(
    SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftSuccess,
    (
      state,
      { key, total, quotaGroups, audience, cpi, suppliers },
    ): InsightsState =>
      patchPreDraftInsightsChild(state, key, (child) => ({
        feasibility: {
          ...child.feasibility,
          total,
          quotaGroups,
          audience,
          cpi,
          suppliers,
          loading: false,
          error: null,
        },
      })),
  ),

  on(
    SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftFailure,
    (state, { key, error }): InsightsState =>
      patchPreDraftInsightsChild(state, key, (child) => ({
        feasibility: { ...child.feasibility, loading: false, error },
      })),
  ),

  on(
    AdvancedFilterActions.clearSurveyOptions,
    (state): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          advancedFiltersLookup: {
            ...child.feasibility.advancedFiltersLookup,
            criteriaSurveyOptions: [],
            groupSurveyOptions: [],
            error: null,
          },
        },
      })),
  ),

  on(
    AdvancedFilterActions.resetFilters,
    (state): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          advancedFiltersLookup:
            DEFAULT_CHILD_INSIGHTS_STATE.feasibility.advancedFiltersLookup,
        },
      })),
  ),

  on(
    AdvancedFilterApiActions.findSurveysSuccess,
    (state, { surveys, psidRefsBySurveyId }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          advancedFiltersLookup: {
            ...child.feasibility.advancedFiltersLookup,
            matchedGroups: surveys,
            psidRefsBySurveyId: {
              ...child.feasibility.advancedFiltersLookup.psidRefsBySurveyId,
              ...psidRefsBySurveyId,
            },
            error: null,
          },
        },
      })),
  ),

  on(
    AdvancedFilterApiActions.findSurveysFailure,
    (state, { error }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          advancedFiltersLookup: {
            ...child.feasibility.advancedFiltersLookup,
            error,
          },
        },
      })),
  ),

  on(
    AdvancedFilterApiActions.respondentCountsSuccess,
    (state, { totalCount }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          advancedFiltersLookup: {
            ...child.feasibility.advancedFiltersLookup,
            respondentCount: totalCount,
            error: null,
          },
        },
      })),
  ),

  on(
    AdvancedFilterApiActions.respondentCountsFailure,
    (state, { error }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          advancedFiltersLookup: {
            ...child.feasibility.advancedFiltersLookup,
            respondentCount: 0,
            error,
          },
        },
      })),
  ),

  on(
    AdvancedFilterApiActions.searchCriteriaSurveysSuccess,
    (state, { surveys }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          advancedFiltersLookup: {
            ...child.feasibility.advancedFiltersLookup,
            criteriaSurveyOptions: surveys,
            error: null,
          },
        },
      })),
  ),

  on(
    AdvancedFilterApiActions.searchCriteriaSurveysFailure,
    (state, { error }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          advancedFiltersLookup: {
            ...child.feasibility.advancedFiltersLookup,
            error,
          },
        },
      })),
  ),

  on(
    AdvancedFilterApiActions.searchGroupSurveysSuccess,
    (state, { surveys, psidRefsBySurveyId }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          advancedFiltersLookup: {
            ...child.feasibility.advancedFiltersLookup,
            groupSurveyOptions: surveys,
            psidRefsBySurveyId: {
              ...child.feasibility.advancedFiltersLookup.psidRefsBySurveyId,
              ...psidRefsBySurveyId,
            },
            error: null,
          },
        },
      })),
  ),

  on(
    AdvancedFilterApiActions.searchGroupSurveysFailure,
    (state, { error }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        feasibility: {
          ...child.feasibility,
          advancedFiltersLookup: {
            ...child.feasibility.advancedFiltersLookup,
            error,
          },
        },
      })),
  ),

  // Also hydrates pricing.offerPrice/surveyPauseThreshold from these prices —
  // this fires on survey load and every recalculation, so a save made without
  // ever opening the CPI drawer still carries the survey's existing pricing
  // instead of wiping it with `null`. Apply overwrites it afterwards.
  on(
    SurveyCreationInsightsApiActions.loadFlexibilityPricesSuccess,
    (state, { prices }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        flexibilityPrices: { prices, error: null },
        pricing: {
          ...child.pricing,
          offerPrice: prices.surveyPriceOverride,
          surveyPauseThreshold: prices.surveyPauseThreshold,
        },
      })),
  ),

  on(
    SurveyCreationInsightsActions.applyCpiPricing,
    (
      state,
      { offerPrice, surveyPauseThreshold, blendApplied },
    ): InsightsState =>
      patchActiveInsightsChild(state, {
        pricing: { offerPrice, surveyPauseThreshold, blendApplied },
      }),
  ),

  on(
    SurveyCreationInsightsApiActions.loadFlexibilityPricesFailure,
    (state, { error }): InsightsState =>
      patchActiveInsightsChild(state, {
        flexibilityPrices: { prices: null, error },
        pricing: { ...DEFAULT_CHILD_INSIGHTS_STATE.pricing },
      }),
  ),

  on(
    SurveyCreationInsightsApiActions.loadSimilarSurveysPending,
    (state, { searchGlobally }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        similarSurveys: {
          ...child.similarSurveys,
          status: 'loading',
          error: null,
          isStale: false,
          searchedGlobally: searchGlobally,
        },
      })),
  ),

  on(
    SurveyCreationInsightsApiActions.loadSimilarSurveysSuccess,
    (
      state,
      { surveys, searchGlobally, targetingKey, isStale },
    ): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        similarSurveys: {
          surveys,
          status: 'loaded',
          error: null,
          isStale,
          searchedTargetingKey: targetingKey,
          searchedGlobally: searchGlobally,
          globalSearchUsed:
            child.similarSurveys.globalSearchUsed || searchGlobally,
        },
      })),
  ),

  on(
    SurveyCreationInsightsApiActions.loadSimilarSurveysFailure,
    (state, { error }): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        similarSurveys: {
          ...child.similarSurveys,
          status: 'failed',
          error,
          isStale: false,
        },
      })),
  ),

  on(
    SurveyCreationInsightsActions.cancelSimilarSurveysSearch,
    (state): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        similarSurveys: {
          ...child.similarSurveys,
          status: child.similarSurveys.searchedTargetingKey ? 'loaded' : 'idle',
          error: null,
          isStale: !!child.similarSurveys.searchedTargetingKey,
        },
      })),
  ),

  on(
    SurveyCreationInsightsActions.markSimilarSurveysStale,
    (state): InsightsState =>
      patchActiveInsightsChild(state, (child) => ({
        similarSurveys: { ...child.similarSurveys, isStale: true },
      })),
  ),

  on(
    SurveyCreationActions.reset,
    (): InsightsState => INITIAL_SURVEY_CREATION_INSIGHTS_STATE,
  ),
);
